/*
    The Allocations Bug:
    Shipment with two lines was partially worked i.e...
    1. CA-001 Order OrderQty: 5  ShippedQty: 0  -- no Allocations
    2. CA-002 Order OrderQty: 25 ShippedQty: 15 -- One Allocation with Qty=15

    Shipment is resumed and additional Allocations entered
    1. CA-001 Order OrderQty: 5  ShippedQty: 0  -- new Allocation with Qty=5
    2. CA-002 Order OrderQty: 25 ShippedQty: 20 -- prev Allocation with Qty=15, new Allocation with Qty=10

    If prev Allocation of Line.2 is sent, Allocation specified for Line.1 receives latter Qty of 15 and will result in "Unassigned Qty" or "Insufficient Qty"
*/

const acceptAdjustmentsLessThanNYards = 5;
const acceptAdjustmentsWithinNPercent = 0.1;

import { Component, Directive, EventEmitter, Injectable, Output, isDevMode } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Observable } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialog } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { WarehouseSelectDialog } from '../warehouse-select-dialog/warehouse-select-dialog.component';

import { AcumaticaRequestOptions, AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService } from './acumatica-entity';
import { Acumatica, AcumaticaFieldFormat } from '../acumatica-field-decorator';
import { AcumaticaLoadingOptions, LoadingIndicator } from '../loading-indicator/AcumaticaLoadingOptions';
import { AcumaticaError, CancelActionError, NotFoundError } from '../acumatica-errors';

import { StockItem } from './StockItem.service';
import { InventorySummaryInquiry } from './InventorySummaryInquiry.service';
import { SalesOrderService, SalesOrder, SalesOrderShipment } from './SalesOrder.service';
import { Adjustment, AdjustmentDetail, AdjustmentService } from './Adjustment.service';
import { Transfer, TransferDetail, TransferService } from './TransferOrder.service';
import { environment } from 'src/environments/environment';
import { WarehouseService } from './Warehouse.service';

// #region Inventory inquiry & extended encapsulation class

@Injectable({ providedIn: 'root' })
export class InventorySourceService extends AcumaticaEntityService {
    constructor(acumatica: AcumaticaService) {
        super(acumatica, 'InventorySummaryInquiry', InventorySourceInquiry);
    }

    get(
        InventoryID: string,
        WarehouseID: string | null = null,
        LocationID: string | null = null,
        options: AcumaticaRequestOptions = {}
    ): Observable<InventorySource[]> {
        return new Observable<InventorySource[]>(subscriber => {
            options.noCache = true; // never cache Inventory data

            let inquiry = new InventorySourceInquiry({
                InventoryID: InventoryID,
                WarehouseID: WarehouseID,
                LocationID: LocationID
            });

            super.putEntity(inquiry, {
                ...options,
                ...{ expand: "Results" }
            }).subscribe({
                next: (value: InventorySourceInquiry) => {
                    value.Results.forEach(result => result.OriginalLocationID = result.LocationID);
                    subscriber.next(value.Results);
                },
                error: err => subscriber.error(err),
                complete: () => subscriber.complete()
            });
        });
    }
}

@Directive()
export class InventorySource extends AcumaticaEntity {
    @Acumatica() LotSerialNbr!: string | null;
    @Acumatica({ reader: (value: boolean | null) => value === true }) PFASFree!: boolean;

    constructor(init?: any, defaults?: any) { super(init, defaults); }

    @Acumatica('LotHash')
    LotSerialHash: string | null = null;

    @Acumatica() BaseUOM!: string | null;
    stockItem!: StockItem;
    get InventoryID(): string { return this.stockItem.InventoryID; }

    private _LocationID!: string | null;
    @Acumatica() get LocationID() { return this._LocationID; }
    set LocationID(value) {
        if (this._LocationID == value) return;
        this._LocationID = value;
        this.OnLocationIDChanged.emit();
    }
    @Output() OnLocationIDChanged = new EventEmitter();

    OriginalLocationID!: string | null;
    get isTransfer(): boolean { return this.LocationID?.trim().toUpperCase() != this.OriginalLocationID?.trim().toUpperCase(); }

    /** the quantity of the given Lot # Pre-Allocated on other Sales Orders; showing in inquiry as Location: <UNASSIGNED> and merged into main line*/
    AltSOPreAllocated: number = 0;

    private _qtyOnHand!: number;
    @Acumatica()
    get QtyOnHand(): number { return this._qtyOnHand - this.TotalUsageQty(true); }
    set QtyOnHand(value: number) { this._qtyOnHand = value; }
    /** the Quantity On Hand from Acumatica; excluding usage on the current shipment */
    get AcumaticaQtyOnHand(): number { return this._qtyOnHand; }

    private _qtyAvailable!: number;
    @Acumatica()
    get QtyAvailable(): number { return this._qtyAvailable - this.TotalUsageQty(false); }
    set QtyAvailable(value: number) { this._qtyAvailable = value; }

    /** includes quantity on other Open shipments */
    @Acumatica() SOAllocated!: number;
    @Acumatica() CutCount!: number | null;
    @Acumatica() InventoryDate!: Date | null;
    @Acumatica() InventoryUser!: string | null;
    @Acumatica({ reader: value => !!value }) StockCheckRequested!: boolean;
    @Acumatica() StockCheckReqDate!: Date | null;
    @Acumatica() StockCheckRequestor!: string | null;
    @Acumatica({
        reader: (value: string) => (value || '') == '' ? null : (value.toLowerCase() == 'yes')
    }) CommercialMatch: boolean | null = null;

    /** return a value indicating if the inventory piece needs to be rolled out and measured */
    get rolloutRequired(): boolean {
        if (!this.stockItem.isLotSerItem) return false;

        // if the piece has never been inventoried (InventoryDate==null), rollout after 3rd cut
        // if the piece has been invenotried at least once (InventoryDate!=null), rollout after every 5th cut
        return (this.CutCount || 0) >= (this.InventoryDate == null ? 2 : 4) && this.RollBalance != 0;
    }

    /** the available remaining quantity of the roll */
    get RollBalance(): number {
        let usage = this.TotalUsageQty(false);
        return this._qtyAvailable + this.AltSOPreAllocated - usage;
    }

    // #region Adjustments

    @Output() OnAdjustmentQtyChanged = new EventEmitter();
    _AdjustmentQty: number | undefined;
    /** the adjustment to make; undefined for no adjustment */
    get AdjustmentQty(): number | undefined {
        if (this._AdjustmentQty === undefined) return undefined;
        return this.stockItem.roundQty(this._AdjustmentQty) || 0;
    }
    set AdjustmentQty(value: number | undefined) {
        this._AdjustmentQty = value;
        this.OnAdjustmentQtyChanged.emit();
    }
    /** returns a boolean value indicating if the inventory piece has an adjustment */
    get hasAdjustment(): boolean { return this.AdjustmentQty !== undefined; }
    /** the adjustment quantity coalesed to zero */
    get AdjustmentAmt(): number { return this.AdjustmentQty || 0; }
    get AdjQtyOnHand(): number { return this.QtyOnHand + this.AdjustmentAmt; }
    get AdjQtyAvailable(): number { return this.QtyAvailable + this.AdjustmentAmt; }
    /** returns the Qty of this Piece being used for Pre-Allocated lines on this Shipment */
    get PreAllocatedQty(): number {
        return this._usage.reduce((prev, alloc) => prev + (alloc.shipmentDetail.PreAllocation?.Quantity || 0), 0);
    }

    /** gets/sets the AdjustmentQty to the appropriate value so the system Qty Available is the quantity indicated */
    get AdjustRemainderTo(): number | null {
        if (this._AdjustmentQty === undefined) return null;
        return this.QtyAvailable + this.AdjustmentAmt;
    }
    set AdjustRemainderTo(AdjustTo: number | null) {
        if (AdjustTo === null) { this.AdjustmentQty = undefined; return; }
        if (typeof AdjustTo == 'string') AdjustTo = parseFloat(AdjustTo);
        this.AdjustmentQty = AdjustTo - this.RollBalance;
    }

    /** returns a boolean value indicating true if the adjustment quantity is within acceptable threshold (less than 5 yards or 10% of roll balance) */
    public get AdjustmentAcceptable(): boolean {
        if (this.AdjustmentQty === undefined) return true;
        let adjustmentAmt = Math.abs(this.AdjustmentAmt);
        if (adjustmentAmt <= acceptAdjustmentsLessThanNYards) return true;

        return this.AdjustmentAmt > this.RollBalance * (1 - acceptAdjustmentsWithinNPercent)
            && this.AdjustmentAmt < this.RollBalance * (1 + acceptAdjustmentsWithinNPercent);
    }

    // #endregion

    // #region Piece Usage

    private _usage: ShipmentDetailAllocation[] = [];
    /** count of Shipment Details using the piece as a pick source */
    get UsageCount() { return this._usage.length; }
    AddUsage(allocation: ShipmentDetailAllocation) {
        this._usage.push(allocation);
    }
    RemoveUsage(allocation: ShipmentDetailAllocation) {
        this._usage.remove(allocation);
    }

    /**
     * Total Picked quantity the Inventory Source is being used for within the Shipment.
     * (Pre-Allocation reductions are already added into the Qty Available)
    */
    TotalUsageQty(includePreAllocations: boolean): number {
        return this._usage.sum((usage: ShipmentDetailAllocation) =>
            // (usage.Qty || 0)
            // - ((includePreAllocations || !usage.shipmentDetail.PreAllocation)
            //     // not including pre-allocation quantities or there isn't one
            //     ? 0
            //     // in case a PreAllocation includes an allowance (i.e. Flaw) need to include that allowance in "usage" quantity
            //     : usage.shipmentDetail.PreAllocation!.Quantity
            // )

            (usage.Qty || 0)
            + ((includePreAllocations && usage.shipmentDetail.PreAllocation)
                // in case a PreAllocation includes an allowance (i.e. Flaw) need to include that allowance in "usage" quantity
                ? usage.shipmentDetail.PreAllocation!.Quantity
                // not including pre-allocation quantities or there isn't one
                : 0
            )
        );
    }

    // #endregion
}

export class InventorySourceInquiry extends AcumaticaEntity {
    @Acumatica() InventoryID!: string;
    @Acumatica() LocationID!: string | null;
    @Acumatica() WarehouseID!: string | null
    @Acumatica() ExpandByLotSerialNbr: boolean = true;

    @Acumatica(InventorySource) Results!: InventorySource[];

    constructor(inquiry?: Partial<InventorySummaryInquiry>) {
        super();
        Object.assign(this, inquiry);
    }
}

// #endregion

@Injectable({ providedIn: 'root' })
export class OrderPickingService extends AcumaticaEntityService {
    constructor(
        private dialog: MatDialog,
        protected acumatica: AcumaticaService,
        private salesOrders: SalesOrderService,
        private adjustments: AdjustmentService,
        private transfers: TransferService,
        private warehouses: WarehouseService,
    ) {
        super(acumatica, 'Shipment', Shipment);
    }

    get(
        ShipmentNbr: string,
        options: AcumaticaRequestOptions = {}
    ): Observable<Shipment> {
        options.noCache = true; // never cache Shipments

        return new Observable<Shipment>(subscriber => {

            super.getEntity(ShipmentNbr, {
                ...{
                    expand: ['Details', 'Details/Allocations', 'PreAllocations'],
                    loader: { message: "Getting Shipment" }
                },
                ...options,
            }).subscribe({
                next: (shipment: Shipment) => {

                    // assign PreAllocation object references to corresponding ShipmentDetail.PreAllocation property
                    shipment.PreAllocations?.forEach(preAlloc => {
                        let detail = shipment.Details?.find(detail => detail.LineNbr == preAlloc.LineNbr);
                        if (detail) detail.PreAllocation = preAlloc;
                    })
                    subscriber.next(shipment);

                },
                error: error => subscriber.error(error),
                complete: () => subscriber.complete()
            });
        });
    }

    search(filter: string, options: AcumaticaRequestOptions = {}): Observable<Shipment[]> {
        return super.getEntities(filter, options);
    }

    put(shipment: Shipment, options?: AcumaticaRequestOptions): Observable<Shipment> {
        return new Observable<Shipment>(subscriber => {
            super.putEntity(shipment, options).subscribe(
                updatedShipment => {
                    subscriber.next(updatedShipment);
                    subscriber.complete();
                },
                err => {
                    subscriber.error(err);
                    subscriber.complete();
                }
            );
        });
    }

    createShipment(
        SOOrderType: string,
        SOOrderNbr: string,
        warehouseID: string | undefined = undefined,
        showLoadingIndicators: boolean = true
    ): Observable<SalesOrderShipment> {
        return new Observable<SalesOrderShipment>(subscriber => {
            let errorHandler = (error: AcumaticaError) => {
                if (error instanceof NotFoundError) error.message = "Invalid Sales Order Number";
                subscriber.error(error);
            };

            // get the sales order with current list of Shipments
            let getSOOpts: any = {
                select: ['OrderType', 'OrderNbr', 'Status', 'ShipmentDate'],
                expand: ['Shipments', 'Details'],
            };

            if (!showLoadingIndicators) getSOOpts.loader = null;

            this.salesOrders.get(SOOrderType, SOOrderNbr, getSOOpts, false)
                .subscribe((salesOrder: SalesOrder) => {
                    switch (salesOrder.Status) {
                        case 'Credit Hold':
                        case 'On Hold':
                            subscriber.error(new Error('Can not create Shipment\nSales Order Status is ' + salesOrder.Status));
                            return;
                    }

                    // check the SO for open Shipments
                    const openShipment = salesOrder.Shipments?.find(shipment => shipment.Status == 'Open');
                    if (openShipment) {
                        this.dialog.open(ConfirmationDialog, {
                            data: {
                                message: "There is already an open shipment for this Sales Order"
                                    + "\n\nOpen existing Shipment <em>" + openShipment.ShipmentNbr + "</em>?",
                                confirmText: 'Yes'
                            }
                        }).afterClosed().subscribe((openExisting: boolean) => {
                            if (openExisting)
                                subscriber.next(openShipment);
                            else
                                subscriber.error(new CancelActionError())
                        })
                        return;
                    }


                    // check if SO contains lines from multiple warehouses
                    // if so, dialog (async) prompt to select warehouse
                    (new Promise<string>((resolve, cancel) => {
                        if (warehouseID !== undefined) { resolve(warehouseID); return; }

                        let warehouseIDs = salesOrder.Details!
                            .filter(soline => !soline.Completed)
                            .map(soline => soline.WarehouseID)
                            .unique<string>();
                        if (warehouseIDs.length == 0) {
                            this.dialog.open(AlertDialog, {
                                data: {
                                    message: "There are no lines on the Order!"
                                }
                            });
                            cancel();
                        }
                        else if (warehouseIDs.length === 1) {
                            resolve(warehouseIDs[0]);
                        } else {
                            let dialogRef = this.dialog.open(WarehouseSelectDialog, {
                                data: {
                                    icon: 'Help',
                                    warehouseIDs: warehouseIDs,
                                    message: "Order contains lines from multiple warehouses.\nSelect Warehouse for Shipment:"
                                }
                            });
                            dialogRef.afterClosed().subscribe(response => {
                                if (response) resolve(response)
                                else cancel();
                            });
                        }
                    }))
                        .then(warehouseID => {
                            // have Acumatica create the Shipment
                            var today = new Date();

                            this.acumatica.action(
                                'SalesOrder', 'SalesOrderCreateShipment',
                                { "OrderType": salesOrder.OrderType, "OrderNbr": salesOrder.OrderNbr },
                                { "ShipmentDate": today > salesOrder.ShipmentDate ? today : salesOrder.ShipmentDate, "WarehouseID": warehouseID },
                                showLoadingIndicators ? { message: "Creating Shipment" } : null
                            ).subscribe(() => {
                                // CreateShipment action completed -- get SO again
                                this.salesOrders.get(SOOrderType, SOOrderNbr, {
                                    ...getSOOpts,
                                    ...{
                                        noCache: true,
                                        loader: showLoadingIndicators ? { message: "Getting Created Shipment Nbr" } : null
                                    }
                                }).subscribe((newSO: SalesOrder) => {
                                    // search for the Shipment that didn't exist in the SO previously
                                    let newShipment = newSO.Shipments!.find(shipment => {
                                        return salesOrder.Shipments!.find(prevShipment => {
                                            return prevShipment.ShipmentNbr == shipment.ShipmentNbr
                                        }) === undefined;
                                    });
                                    if
                                        (newShipment) subscriber.next(newShipment);
                                    else
                                        subscriber.error(new Error("It appears the Shipment failed to create"));
                                });
                            }, errorHandler);
                        })
                        .catch(() => {
                            subscriber.error(new CancelActionError());
                        });
                }, errorHandler)
        });
    }

    printShipmentConfirmation(shipmentNbr: string, loader?: AcumaticaLoadingOptions): Promise<void>;
    printShipmentConfirmation(shipment: Shipment, loader?: AcumaticaLoadingOptions): Promise<void>;
    printShipmentConfirmation(shipment: string | Shipment, loader: AcumaticaLoadingOptions | null = null): Promise<void> {
        const entity = {
            ShipmentType: 'Shipment',
            ShipmentNbr: (shipment instanceof Shipment) ? shipment.ShipmentNbr : shipment
        }
        return new Promise<void>((resolve, reject) => {
            this.acumatica.action('Shipment', 'PrintShipmentConfirmationToWhse', entity, loader)
                .subscribe(
                    () => { resolve(); },
                    error => reject(error)
                );
        });
    }

    printQualityControlLabels(shipmentNbr: string, printer: string, loader?: AcumaticaLoadingOptions): Promise<void>;
    printQualityControlLabels(shipment: Shipment, printer: string, loader?: AcumaticaLoadingOptions): Promise<void>;
    printQualityControlLabels(shipment: string | Shipment, printer: string, loader?: AcumaticaLoadingOptions): Promise<void> {
        const entity = {
            ShipmentType: 'Shipment',
            ShipmentNbr: (shipment instanceof Shipment) ? shipment.ShipmentNbr : shipment
        }
        const params = {
            DirectPrint: true,
            Printer: printer
        };
        return new Promise<void>((resolve, reject) => {
            this.acumatica.action('Shipment', 'PrintQualityControlLabels', entity, params, loader)
                .subscribe(
                    () => { resolve(); },
                    err => reject(err)
                );
        });
    }

    setCurrentPickerID(shipment: Shipment, PickerID: string | null = this.acumatica.username) {
        if (PickerID?.toUpperCase() == 'NICKH') return;

        // update the original object
        shipment.PickerID = PickerID;

        // create a small Shipment object to update Acumatica
        let updateShipment = new Shipment();
        updateShipment.Type = shipment.Type;
        updateShipment.ShipmentNbr = shipment.ShipmentNbr;
        updateShipment.PickerID = PickerID;

        this.acumatica.put('Shipment', updateShipment, { loader: null }).subscribe();
    }

    // #region Shipment Confirmation

    completeShipmentPicking(
        shipment: Shipment,
        // CustomerSpecificTagPrinterID: string | null | undefined,
        isComplete: boolean,
        CutAndHoldLocationID: string,
        UpdateStatusCallback?: (status: CompleteShipmentStatus) => void,
    ): Observable<void> {
        return new Observable(subscriber => {
            // #region Prepare Shipment for posting

            // sort the lines back into Line Nbr order (from Location order)
            // necessary to add Allowances in proper sequence
            shipment.Details = shipment.Details.sort((a, b) => a.LineNbr! - b.LineNbr!);

            // // initialize array for files to be uploaded
            let putFiles: [entityName: string, key: string | string[], file: any, filename?: string, options?: AcumaticaRequestOptions][] = [];

            // define extended SaleOrder class with an additional isModified boolean flag
            class ExtendedSalesOrder extends SalesOrder { isModified: boolean = false };

            // #endregion

            // #region Get Sales Orders

            // dictionaries of "SOOrderType/SOOrderNbr" to Promise<SalesOrder> and SalesOrder entities
            let SalesOrderEntities: { [key: string]: Promise<ExtendedSalesOrder> } = {}
            let SalesOrders: { [key: string]: SalesOrder } = {}

            // Get a Sales Order entity from Acumatica as a Promise re-using same Promises if already being fetched            
            let GetSalesOrder = (orderType: string, orderNbr: string, statusFeedback?: CompleteShipmentStatus): Promise<ExtendedSalesOrder> => {
                let key = orderType + '/' + orderNbr;
                if (SalesOrderEntities[key] == undefined) {
                    SalesOrderEntities[key] = new Promise<ExtendedSalesOrder>(resolve => {
                        if (statusFeedback) statusFeedback.Details = key;
                        this.salesOrders.get(orderType, orderNbr, { expand: 'Details', noCache: true, loader: null }).subscribe(so => {
                            SalesOrders[key] = so;
                            let modifiedSO = so as ExtendedSalesOrder;
                            modifiedSO.isModified = false;
                            resolve(modifiedSO);
                        }, error => { errHandler(error); return; })
                    });
                }
                return SalesOrderEntities[key];
            }

            // #endregion

            // #region Prepare Allowances (EOR, PCS, FLA)

            // initialize array for Allowance details (EOR, PCS, FLA) to be appended to SO
            let newAllowances: Allowance[] = shipment.Details.reduce((accumulator: Allowance[], detail) => accumulator.concat(detail.GetAllowances()), []);
            // keep track of new Allowance SOLines getting added per Sales Order
            let SONewAllowances: { [key: string]: Allowance[] } = {};

            // // TODO: Flaw image handling
            // if (alloc.FLAImgs !== undefined) {
            //     alloc.FLAImgs.forEach((image, imageIndex) => {
            //         let filename = "Flaw - Line " + detail.LineNbr;
            //         if (alloc.FLAImgs.length > 1) {
            //             filename += " (" + (imageIndex + 1) + ")";
            //         }
            //         filename += image.file.name.substring(image.file.name.lastIndexOf('.'));
            //         putFiles.push([
            //             'Shipment',
            //             mainShipment.ShipmentNbr,
            //             image.file,
            //             filename,
            //             { loader: null }
            //         ]);
            //     });
            // }

            // #endregion

            // #region Prepare/Modify SO Lines (FULL)

            // iterate Shipment Details SO lines to be directly modified (FULL roll items)
            shipment.Details
                // for FULL roll shipments
                .filter(detail => detail.ShipFullRolls && detail.fulfillmentQty != 0)
                .forEach(detail => {
                    // // Update the Shipment Ordered Qty
                    // detail.OrderedQty = detail.PickedQty;

                    // Get the Sales Order document
                    let initialSalesOrderRetrievalPromise = GetSalesOrder(detail.OrderType, detail.OrderNbr);

                    // replace the Promise in the dictionary with one resolved after the SO is modified
                    SalesOrderEntities[detail.OrderType + '/' + detail.OrderNbr] = new Promise<ExtendedSalesOrder>(resolve => {
                        // when the original SO is retrieved
                        initialSalesOrderRetrievalPromise.then(so => {
                            // find the Detail line
                            let soline = so.Details!.find(soline => soline.LineNbr == detail.OrderLineNbr)!;
                            let shippedQty = detail.Allocations.map(alloc => alloc.FulfillmentQty).sum();
                            if (shippedQty != soline.OrderQty) {
                                soline.OrderQty = shippedQty;
                                so.isModified = true;
                            }
                            resolve(so);
                        });
                    });
                });
            // #endregion

            // #region Generate update action plan (steps) & Feedback object

            // function to handle errors from API processes relaying it to the status feedback indicator
            const errHandler = (error: any) => {
                console.error(error);
                status.error = error;
                UpdateStatusCallback && UpdateStatusCallback(status);
                subscriber.error({ error: error, status: status });
            }

            // initialize Shipment Status feedback object
            let adjustmentDocument = shipment.BuildAdjustmentDocument();
            let transferDocument = shipment.BuildTransferDocument();

            // Cut & Hold
            if (shipment.CutAndHold) {
                shipment.Details.filter(detail => detail.InventoryID != 'TARIFF-FEE').forEach(detail => {
                    detail.Allocations.forEach(alloc => {
                        let fromLocation = alloc.LocationID!.toString();
                        let toLocation = CutAndHoldLocationID;
                        if (fromLocation == toLocation) return;

                        // 1. Add Transfer Details to the Transfer Document for all the pieces
                        let transfer =  new TransferDetail({
                            InventoryID: detail.InventoryID,
                            LotSerialNbr: alloc.LotSerialNbr,
                            FromLocationID: fromLocation,
                            ToLocationID: toLocation,
                            Qty: alloc.Qty
                        });
                        transferDocument!.Details.push(transfer);

                        // // * Cutters may cut the same original piece multiple times (of multiple yardages)
                        // //   Transfer details per InventoryID+LotSerialNbr+From+To need aggregated

                        // // Locate an existing transfer line
                        // let transfer = transferDocument!.Details.find(transfer =>
                        //     transfer.InventoryID == detail.InventoryID &&
                        //     transfer.LotSerialNbr == alloc.LotSerialNbr &&
                        //     transfer.FromLocationID == fromLocation &&
                        //     transfer.ToLocationID == toLocation
                        // );
                        // // If an existing one doesn't exist, initialize a new one
                        // if(transfer == undefined) {
                        //     transfer =  new TransferDetail({
                        //         InventoryID: detail.InventoryID,
                        //         LotSerialNbr: alloc.LotSerialNbr,
                        //         FromLocationID: fromLocation,
                        //         ToLocationID: toLocation,
                        //         Qty: 0
                        //     });

                        //     transferDocument!.Details.push(transfer);
                        // }
                        // // Add the transfer quantity
                        // transfer.Qty += alloc.Qty ?? 0;

                        // 2. Update allocation with data directly and detach Allocation from Inventory Source
                        alloc.LocationID = toLocation;
                        // alloc.source will be null if loading a partially worked shipment
                        if (alloc.source) alloc.LotSerialNbr = alloc.source.LotSerialNbr;
                        alloc.source = undefined;
                    });
                });
            }

            if (false && isDevMode()) {
                console.warn('adjustment', adjustmentDocument);
                console.warn('transfer', transferDocument);
                subscriber.error('OPERATION ABORTED');
                subscriber.complete();
                return;
            }

            let status = new CompleteShipmentStatus(shipment, newAllowances, adjustmentDocument, transferDocument);

            // TODO: Flaw image handling
            // status.UploadImages =
            //     shipment.Details.filter(detail => detail.Allocations.filter(alloc => alloc.FLAImgs !== undefined && alloc.FLAImgs.length > 0).length > 0).length == 0
            //         ? CompleteShipmentStepStatus.Omitted
            //         : CompleteShipmentStepStatus.Pending;

            // OBSOLETE -- revised to print Customer-specific roll tags from Acumatica Action with each Customer Roll Tag
            // status.CustomerSpecialRollTags = (!CustomerSpecificTagPrinterID)
            //     ? CompleteShipmentStepStatus.Omitted
            //     : CompleteShipmentStepStatus.Pending;
            status.CustomerSpecialRollTags = CompleteShipmentStepStatus.Omitted;

            // print '03' Pack List when shipment is [Complete] (not updating partial)
            status.PrintPacklist = isComplete && shipment.WarehouseID == '03'
                ? CompleteShipmentStepStatus.Pending : CompleteShipmentStepStatus.Omitted;

            // relay updated events to callback function
            status.updated.subscribe(() => UpdateStatusCallback && UpdateStatusCallback(status));

            // #endregion

            // #region API Acumatica Updates
            (async () => {

                //#region 1. Create and Release the Inventory Adjustments
                // must be done first in case of increasing quantity on hand
                if (adjustmentDocument?.Details.length) {
                    status.InventoryAdjustments = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(adjustments_complete => {

                            console.log('Create & Release Adjustments');

                            this.adjustments.put(adjustmentDocument!, { loader: null }).subscribe(newAdjustment => {
                                if (this.acumatica.disableAdjustmentRelease) adjustments_complete();
                                else this.adjustments.Release(newAdjustment, null).subscribe(() => { adjustments_complete(); });
                            }, error => { errHandler(error); return; });

                        });
                    })();

                    // on each Allocation in the shipment set the Inventoried flag to whether an adjustment has been made to the source or not
                    shipment.Details.forEach(detail =>
                        detail.Allocations.forEach(alloc => alloc.Inventoried = alloc.source?.AdjustmentQty !== undefined)
                    );
                    // clear the Adjustments in case of subsequent error and user re-submitting shipment
                    shipment.ClearAllAdjustments();

                    status.InventoryAdjustments = CompleteShipmentStepStatus.Complete;
                }
                // #endregion

                //#region 2. Modify existing SO lines (FULL roll shipments)
                // does return the modified SO after PUT
                if (status.ModifySOLines == CompleteShipmentStepStatus.Pending) {
                    status.ModifySOLines = CompleteShipmentStepStatus.InProcess;

                    await (() => {
                        return new Promise<void>(salesOrderFullRollsUpdateDone => {
                            let ListOfAllSOsBeingUpdated: Promise<SalesOrder | void>[] = [Promise.resolve()];
                            // iterate all the SOs in the dictionary
                            Object.keys(SalesOrderEntities).forEach(key => {
                                let SOUpdatedPromise = SalesOrderEntities[key];

                                // update the Promise in the dictionary to one resolved when the SO update is done
                                SalesOrderEntities[key] = new Promise<ExtendedSalesOrder>(resolve => {

                                    // after the SO is read and possibly modified by the FULL-roll initial handling above...
                                    SOUpdatedPromise.then(so => {
                                        // if it has actually changed (FULL roll quantity was adjusted)
                                        if (so.isModified) {
                                            // update Acumatica Sales Order
                                            status.Details = key;
                                            this.salesOrders.put(so, {
                                                expand: 'Details',
                                                loader: null
                                            }).subscribe(so => {
                                                // clear FULL flag from detail lines (in case subsequent error and user tries to Complete again)
                                                shipment.Details.forEach(detail => detail.ShipFullRolls = false)

                                                // recalculate any Customer Discounts on new quantities
                                                this.acumatica.action('SalesOrder', 'AutoRecalculateDiscounts', so).subscribe(() => {
                                                    // resolve promise with the updated SO
                                                    resolve(so as ExtendedSalesOrder);
                                                })
                                            }, error => { errHandler(error); return; });
                                        }
                                        else resolve(so);
                                    });

                                });

                                // wait for all SO updates to complete
                                ListOfAllSOsBeingUpdated.push(SalesOrderEntities[key])
                            });

                            Promise.all(ListOfAllSOsBeingUpdated).then(() => salesOrderFullRollsUpdateDone());
                        });
                    })();
                    status.ModifySOLines = CompleteShipmentStepStatus.Complete;
                }
                // #endregion

                //#region 3. Add new Allowance line details to the SO through [Add Shipped Item] Action
                if (status.AddAllowancesToSO == CompleteShipmentStepStatus.Pending) {
                    status.AddAllowancesToSO = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(allowances_done_adding => {

                            console.log('Add Allowance lines to SO');
                            let allowanceIdx = -1;
                            // define self-invoking function to add lines sequentially
                            let addNextItem = () => {
                                if (++allowanceIdx < newAllowances.length) {
                                    let allowance = newAllowances[allowanceIdx];

                                    let soKey = allowance.shipmentDetail.SOKey;
                                    if (SONewAllowances[soKey] === undefined) SONewAllowances[soKey] = [];
                                    SONewAllowances[soKey].push(allowance);

                                    // status feeback
                                    status.Details = `${allowance.shipmentDetail.OrderNbr} - ${allowance.InventoryID} x${allowance.Qty} ${allowance.shipmentDetail.UOM}`;
                                    // add allowance to SO through Action
                                    this.salesOrders.addShippedItem(
                                        allowance.shipmentDetail.OrderType,
                                        allowance.shipmentDetail.OrderNbr,
                                        allowance.InventoryID,
                                        shipment.WarehouseID,
                                        allowance.Qty,
                                        allowance.IsFree,
                                        allowance.note,
                                        null
                                    ).subscribe(() => {
                                        // clear the created allowance in case of subsequent error and user retries Confirm
                                        allowance.resetFn();

                                        // TODO: reset FLA images when the FLA is posted?
                                        // allowance.MainAllocation.FLAImgs = [];

                                        addNextItem();
                                    }, error => { errHandler(error); return; });
                                }
                                else allowances_done_adding();
                            }
                            // add first line
                            addNextItem();

                        });
                    })();
                    status.AddAllowancesToSO = CompleteShipmentStepStatus.Complete;
                }
                // #endregion

                //#region 4. Fetch modified SOs and map newly created SOLines to the related Allowances
                if (status.GetModifiedSOs == CompleteShipmentStepStatus.Pending) {
                    status.GetModifiedSOs = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(all_modified_SOs_retrieved => {

                            // array of Promises that resolve after an SO with allowances added has been fetched and allowances mapped
                            let ModifiedSOs: Promise<SalesOrder | void>[] = [Promise.resolve()];

                            // iterate each SO that had allowances added
                            Object.keys(SONewAllowances).forEach(soKey => {
                                // unset any previous Promise in the retrieved SOs dictionary so it's re-retrieved following allowance additions
                                delete SalesOrderEntities[soKey];
                                let orderType = soKey.substring(0, soKey.indexOf('/'));
                                let orderNbr = soKey.substring(soKey.indexOf('/') + 1);

                                // fetch the SO
                                let allowanceCount = SONewAllowances[soKey].length;
                                let updatedSO = GetSalesOrder(orderType, orderNbr, status).then(so => {
                                    // for each new Allowance line added to the SO    
                                    SONewAllowances[soKey].forEach((allowance, index) => {
                                        // create the Allowance ShipmentDetail and ShipmentDetailAllocation
                                        let detail = new ShipmentDetail();
                                        detail.OrderType = orderType;
                                        detail.OrderNbr = orderNbr;
                                        detail.OrderLineNbr = so.Details![so.Details!.length - allowanceCount + index].LineNbr;
                                        detail.InventoryID = allowance.shipmentDetail.InventoryID;
                                        detail.FreeItem = allowance.IsFree;
                                        detail.note = allowance.note;
                                        let alloc = detail.NewAllocation(allowance.MainAllocation.source!);
                                        if (shipment.CutAndHold) {
                                            alloc.LocationID = CutAndHoldLocationID;
                                            alloc.LotSerialNbr = allowance.MainAllocation.LotSerialNbr;
                                        }
                                        alloc.Qty = allowance.Qty;

                                        // add the ShipmentDetail to the Shipment document
                                        shipment.Details.push(detail);
                                    });
                                });
                                ModifiedSOs.push(updatedSO);
                            })

                            // when all of the Promises in the array are resolved, this stop is complete
                            Promise.all(ModifiedSOs).then(() => { all_modified_SOs_retrieved(); })
                        })
                    })();
                    status.GetModifiedSOs = CompleteShipmentStepStatus.Complete;
                }
                // #endregion

                //#region 5. Create & release the Inventory Transfers
                // Inventory Transfers need done before updating Shipment for case of Cut & Hold
                //      Acumatica will throw error on Lot # if it's not available in specified location (only via API)
                //          When attempting to ship goods out from a C&H location, if the Incl Qty Avail is false, goods can't be shipped
                //          Decision made to just Incl Qty Available on C&H locations so updateLocation action is obsolete
                if (transferDocument?.Details.length) {
                    status.InventoryTransfers = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return new Promise<void>(transfers_complete => {
                            console.log('Create & Release Transfer document');
                            this.transfers.put(transferDocument!, { loader: null }).subscribe(newTransfer => {
                                if (shipment.CutAndHold || environment.release_pick_transfers) {

                                    // Cut&Hold shipments must have the [InclQtyAvail] flag enabled for the Location; done via custom Action in Warehouses graph
                                    let no_loading_indicator: AcumaticaLoadingOptions = { indicator: LoadingIndicator.hidden }
                                    if (shipment.CutAndHold) {
                                        // this.warehouses.updateLocation(shipment.WarehouseID, CutAndHoldLocationID, { InclQtyAvail: true }, no_loading_indicator).subscribe(() => {
                                        this.transfers.Release(newTransfer, no_loading_indicator).subscribe(() => {
                                            // this.warehouses.updateLocation(shipment.WarehouseID, CutAndHoldLocationID, { InclQtyAvail: false }, no_loading_indicator).subscribe(() => {
                                            transfers_complete();
                                            // });
                                        });
                                        // });
                                    }
                                    else {
                                        this.transfers.Release(newTransfer, no_loading_indicator).subscribe(() => { transfers_complete(); });
                                    }
                                } else {
                                    transfers_complete();
                                }
                            }, error => { errHandler(error); return; });
                        });
                    })();
                    status.InventoryTransfers = CompleteShipmentStepStatus.Complete;
                }
                // #endregion

                //#region 6. Update the Shipment document
                if (status.UpdateShipment == CompleteShipmentStepStatus.Pending) {
                    status.UpdateShipment = CompleteShipmentStepStatus.InProcess;

                    await (() => {
                        return new Promise<void>(shipment_updated => {
                            this.put(shipment, {
                                expand: ['Details', 'Details/Allocations'],
                                loader: null
                            }).subscribe(new_shipment => {
                                shipment = new_shipment;
                                if (new_shipment.CutAndHold && new_shipment.Status == 'Open') {
                                    this.action(new_shipment, 'PutOnHold', null, { message: "Placing on Hold" }).subscribe(() => {
                                        shipment_updated();
                                    });
                                } else {
                                    shipment_updated();
                                }
                            }, err => {
                                errHandler(err);
                                return;
                            });

                        });
                    })();
                    status.UpdateShipment = CompleteShipmentStepStatus.Complete;
                }
                // #endregion

                // OBSOLETE -- revised to print Customer-specific roll tags from Acumatica Action with each Customer Roll Tag
                // //#region 7. Print the customer special Labels
                // if (status.CustomerSpecialRollTags == CompleteShipmentStepStatus.Pending) {
                //     status.CustomerSpecialRollTags = CompleteShipmentStepStatus.InProcess;
                //     await (() => {
                //         if (!CustomerSpecificTagPrinterID) {
                //             return Promise.resolve();
                //         } else {
                //             return new Promise<void>(pin_tags_printed => {
                //                 return this.acumatica.action(
                //                     'Shipment', 'PrintCustomerSpecialTags',
                //                     shipment,
                //                     { shipment: true, Printer: CustomerSpecificTagPrinterID },
                //                     null
                //                 ).subscribe(() => {
                //                     pin_tags_printed();
                //                 }, error => { errHandler(error); return; })
                //             });
                //         }
                //     })();
                //     status.CustomerSpecialRollTags = CompleteShipmentStepStatus.Complete;
                // }
                // // #endregion

                //#region 8. Print the Pack List
                if (status.PrintPacklist == CompleteShipmentStepStatus.Pending) {
                    status.PrintPacklist = CompleteShipmentStepStatus.InProcess;
                    await (() => {
                        return this.printShipmentConfirmation(shipment).then(() => {
                            status.PrintPacklist = CompleteShipmentStepStatus.Complete;
                        });
                    })();
                }
                // #endregion

                //#region 9. TODO: Flaw image handling
                //     // 10. Upload & attach photos
                //     if (status.UploadImages == CompleteShipmentStepStatus.Pending) {
                //         status.UploadImages = CompleteShipmentStepStatus.InProcess;
                //         for (let idx = 0; idx < putFiles.length; idx++) {
                //             await (() => {
                //                 console.log('Attach photos');
                //                 status.Details = putFiles[idx][3] || null;
                //                 return new Promise<void>(file_uploaded => {
                //                     this.acumatica.putFile.apply(this.acumatica, putFiles[idx])
                //                         .subscribe(
                //                             () => { file_uploaded(); },
                //                             error => { errHandler(error); return; }
                //                         );
                //                 })
                //             })();
                //         }
                //         status.UploadImages = CompleteShipmentStepStatus.Complete;
                //     }
                // #endregion

            })().then(() => {
                subscriber.next();
                subscriber.complete();
            })
            // #endregion
        });
    }

    confirmShipment(shipment: Shipment, options: AcumaticaRequestOptions): Observable<void> {
        return this.acumatica.action('Shipment', 'ConfirmShipment', shipment, options);
    }

    // #endregion
}

@Component({ template: '' })
export class ShipmentDetailAllocation extends AcumaticaEntity {
    @Acumatica({ write: false }) SplitLineNbr?: number;

    private _InventoryID!: string;
    @Acumatica() get InventoryID() { return this.shipmentDetail.InventoryID || this._InventoryID; }
    set InventoryID(value: string) { this._InventoryID = value; }

    private _LocationID: string | null | undefined;
    @Acumatica() get LocationID() { return this._source?.OriginalLocationID || this._LocationID; }
    set LocationID(value: string | null | undefined) { this._LocationID = value; }

    private _LotSerialNbr: string | null | undefined;
    @Acumatica() get LotSerialNbr() { return this._source?.LotSerialNbr || this._LotSerialNbr; }
    set LotSerialNbr(value: string | null | undefined) { this._LotSerialNbr = value; }

    @Acumatica({ read: false }) get LineNbr() { return this.shipmentDetail.LineNbr; }

    /** flag to tell Acumatica if the item was Inventoried (rolled-out & measured) and should not have the Cut Count incremented */
    @Acumatica({ read: false }) Inventoried: boolean = false;

    constructor();
    constructor(detail?: ShipmentDetail)
    constructor(arg1?: ShipmentDetail) {
        super();
        if (arg1 instanceof ShipmentDetail) this.shipmentDetail = arg1;
    }

    /** the parent ShipmentDetail object this Allocation belongs to */
    shipmentDetail!: ShipmentDetail;

    /** the Inventory Summary source the allocation is being taken from */
    private _source: InventorySource | undefined;
    get source() { return this._source; }
    set source(source: InventorySource | undefined) {
        this._source?.RemoveUsage(this);
        source?.AddUsage(this);
        this._source = source;
    }

    /** Allocations established in Acumatica are readonly=true; new Allocations created in WMS are readonly=false */
    get readonly(): boolean {
        return (<any>this).__id !== undefined
            // (pre-allocations will have Qty 0 and may be edited)
            && this._AcumaticaQty !== 0;
    }

    delete() {
        this.shipmentDetail.DeleteAllocation(this);
    }

    // #region Quantities

    QtyUpdated = new EventEmitter<number | null>();
    private _qty: number | null = null;
    /** the total quantity of the piece / cut */
    @Acumatica({
        writer: (value, entity) => (<ShipmentDetailAllocation>entity).FulfillmentQty,
        reader: (value, entity) => (<ShipmentDetailAllocation>entity)._AcumaticaQty = value
    })
    get Qty(): number | null { return this._qty; }
    set Qty(value: number | null) {
        // note the 'Actual Roll Remainder' prior to updating this piece quantity
        var adjustmentTo = this.source?.AdjustRemainderTo;

        if (value === null || value.toString() == '')
            this._qty = null;
        else
            this._qty = parseFloat((this.shipmentDetail?.roundQty(value)! || value).toString());

        // keep the same 'Actual Roll Remainder'
        if (adjustmentTo !== undefined) this.source!.AdjustRemainderTo = adjustmentTo;

        this.QtyUpdated.emit(this._qty);
    }
    private _AcumaticaQty?: number;

    /** the total quantity of any Allowances on the allocation */
    get AllowanceQty(): number { return this.EORQtyVal + this.PCSQtyVal + this.FLAQtyVal; }

    /** boolan value indicating if there are any allowances on the allocation */
    get AnyAllowances(): boolean { return this.EOR || this.PCS || this.FLA; }

    /** the order fulfillment quantity for the allocation; Total Pick Qty less Allowances */
    get FulfillmentQty(): number { return (this.Qty || 0) - this.AllowanceQty; }

    // #endregion

    // #region Allowances

    AllowancesUpdated = new EventEmitter<void>();

    private _EOR: boolean = false;
    get EOR() { return this._EOR; }
    set EOR(value: boolean) { this._EOR = value; this.AllowancesUpdated.emit(); }
    private _EORQty: number | null = null;
    get EORQty() { return this._EORQty; }
    set EORQty(val: number | null) {
        this._EORQty = this.shipmentDetail.roundQty(val || 0) || null;
        this.AllowancesUpdated.emit();
    }
    get EORQtyVal() { return this.EOR && this.EORQty || 0 }

    private _PCS: boolean = false;
    get PCS() { return this._PCS; }
    set PCS(value: boolean) { this._PCS = value; this.AllowancesUpdated.emit(); }
    private _PCSQty: number | null = null;
    get PCSQty() { return this._PCSQty; }
    set PCSQty(val: number | null) {
        this._PCSQty = this.shipmentDetail.roundQty(val || 0) || null;
        this.AllowancesUpdated.emit();
    }
    get PCSQtyVal() { return this.PCS && this.PCSQty || 0 }

    private _FLA: boolean = false;
    get FLA() { return this._FLA; }
    set FLA(value: boolean) { this._FLA = value; this.AllowancesUpdated.emit(); }
    private _FLAQty: number | null = null;
    get FLAQty() { return this._FLAQty; }
    set FLAQty(val: number | null) {
        this._FLAQty = this.shipmentDetail.roundQty(val || 0) || null;
        this.AllowancesUpdated.emit();
    }
    get FLAQtyVal() { return this.FLA && this.FLAQty || 0 }
    FLAImgs: { file: File, src: SafeResourceUrl }[] = [];

    /** create and return an array of Allowance objects for the allocation */
    GetAllowances(): Allowance[] {
        let allowances: Allowance[] = [];

        if (this.EORQtyVal) {
            let allowance = new Allowance(this, this.EORQtyVal,
                "End of Roll Allowance - Line # " + this.shipmentDetail.LineNbr,
                () => {
                    this.Qty! -= this.EORQtyVal;
                    this.EORQty = null;
                }
            );
            allowances.push(allowance);
        }

        if (this.PCSQtyVal) {
            let allowance = new Allowance(this, this.PCSQtyVal,
                "Small Piece Allowance - Line # " + this.shipmentDetail.LineNbr,
                () => { this.Qty! -= this.PCSQtyVal; this.PCSQty = null; }
            );
            allowances.push(allowance);
        }

        if (this.FLAQtyVal) {
            let allowance = new Allowance(this, this.FLAQtyVal,
                "Material Flaw - Line #" + this.shipmentDetail.LineNbr,
                () => { this.Qty! -= this.FLAQtyVal; this.FLAQty = null; }
            );
            allowances.push(allowance);
        }

        return allowances;
    }

    // #endregion
}

@Component({ template: '' })
export class ShipmentDetail extends AcumaticaEntity {
    /** SO OrderType associated with the ShipmentDetail line */
    @Acumatica() OrderType!: string;
    /** SO OrderNbr associated with the ShipmentDetail line */
    @Acumatica() OrderNbr!: string;
    /** SO LineNbr associated with the ShipmentDetail line */
    @Acumatica() OrderLineNbr!: number;
    /** SO Order Key ( "{OrderType}/{OrderNbr}" ) associated with the ShipmentDetail line */
    get OrderKey(): string { return this.OrderType + "/" + this.OrderNbr; }

    @Acumatica() note!: string | null;
    @Acumatica() InventoryID!: string;
    @Acumatica() Description!: string | null;
    @Acumatica() LineNbr: number | null = null;
    // does not get written for ShipmentDetail object; only for ShipLineSplit / Allocation
    // @Acumatica({ write: false }) LotSerialNbr: string | null = null;

    @Acumatica() FreeItem: boolean | null = null;
    @Acumatica({
        writer: (value, entity) => {
            let detail = <ShipmentDetail>entity;
            return detail.isLotSerItem ? undefined : value;
        }
    }) LocationID: string | null = null;
    @Acumatica({ write: false }) UOM!: string;
    @Acumatica({ write: false }) OrderedQty: number = 0;
    @Acumatica({ write: false }) OriginalQty!: number;
    @Acumatica({ write: false }) ShippedQty: number = 0;

    @Acumatica({ write: false }) ItemTrackingMethod?: string;
    @Acumatica({ write: false }) LotSerialClassID?: string;
    @Acumatica({ write: false }) DefaultShipLocationPriority?: number;
    @Acumatica({ write: false }) DefaultShipLocationID?: string;

    @Acumatica({
        type: ShipmentDetailAllocation,
        // when serializing the ShipmentDetailAllocation array for sending to Acumatica,
        // only send rows that are not readonly
        writer: (value: ShipmentDetailAllocation[]) => value.filter(alloc => !alloc.readonly)
    }) Allocations: ShipmentDetailAllocation[] = [];

    constructor() {
        super();
        let self = this;

        // override Allocations push method (used by Entity decoder when getting Shipment from Acumatica)
        (function (fn) {
            self.Allocations.push = (allocation: ShipmentDetailAllocation): number => {
                // provide a reference to this ShipmentDetail object to the allocation
                allocation.shipmentDetail = self;

                // invoke original function to add to array
                let length = fn.apply(self.Allocations, [allocation]);

                // subscribe to qty changed event of allocation detail
                // TODO: allocation.QtyChanged.subscribe(self.AllocationsChanged);

                return length;
            }
        })(this.Allocations.push);
    }

    // #region supplemental 'Order Picking' processing properties

    /** Pre-Allocation Detail for the Shipment Detail object or undefined */
    PreAllocation?: PreAllocationDetail;

    /** Reference to the StockItem of the shipment detail -- set by PrepareForPicking() process */
    item?: StockItem;

    /** Provides a button in pick entry form for quick quanity entry (i.e. full piece, EOR) */
    QuickAction: QuickAction | null = null;

    /** Shipping Full Rolls -- Update SO to total qty shipped */
    ShipFullRolls: boolean = false;

    /** Flag causes pickingComplete to return false to allow adding to complete lines */
    forceIncomplete: boolean = false;

    /** returns the rounding precesion for quantities; Rolls to 0.125, other Lot items to 0.001 */
    get QtyRoundingPrecision(): number { return this.item!.QtyRoundingPrecision; }

    /** round the given value to the nearest increment for the corresponding Item type (0.125 or 0.001) */
    roundQty(value?: number | string): number | undefined | null {
        // it's possible for a ShipmentDetail object to not have a Stock Item reference
        // such as when being fetched from Acumatica following modification
        // in that case, create a StockItem object instance and set the properties necessary for it to perform roudning
        let item = this.item || new StockItem({
            LotSerialClass: this.LotSerialClassID
        });
        return item.roundQty(value);
    }

    /** total pick quantity for all allocations -- including allowances */
    get PickedQty(): number {
        return this.Allocations.sum(alloc => alloc.Qty || 0);
    };
    /** total pick quantity contributing to the order fulfillment -- less allowances */
    get fulfillmentQty(): number {
        return this.Allocations.sum((alloc: ShipmentDetailAllocation) => alloc.FulfillmentQty || 0);
    }
    /** total allowance quantity for all allocation/pieces on the detail line */
    get allowanceQty(): number {
        return this.Allocations.sum((alloc: ShipmentDetailAllocation) => alloc.AllowanceQty);
    }
    /** total quantity still to be picked for the line; Ordered Qty - Fulfillment Qty */
    get OpenQty(): number { return this.OrderedQty - this.fulfillmentQty; }

    /** indicates if picking is "Complete" for the Shimpment Detail -- observes forceInComplete property */
    get isComplete(): boolean {
        if (this.forceIncomplete) return false;

        // picking is complete when pick quanity is greater than or equal to
        // the order quantity, or in the case of sending full rolls 95% of the order qty
        return (this.PickedQty >= this.OrderedQty * (this.ShipFullRolls ? 0.95 : 1));
    }


    /** returns a boolean indicating if the item is Tracked via Lot or Serial #s */
    get isLotSerItem(): boolean { return this.ItemTrackingMethod != 'N'; }

    /** returns a string Key identifier of the shipment detail's originating Sales Order */
    get SOKey(): string { return this.OrderType + '/' + this.OrderNbr; }

    // #endregion

    // #region Suggested Picking source

    /** determine and set the suggested picking source for the shipment detail */
    SuggestPickingSource(inventory: InventorySource[]) {
        if (this.item!.isLotSerItem) {

            // look for the InventorySource to suggest for picking use
            let totalQtyAvail = 0;
            let suggestedPickingSource: InventorySource | undefined = inventory.find(inv => {
                // if the Shipment Detail was pre-allocated, that piece must be used
                if (this.PreAllocation)
                    return inv.LotSerialNbr == this.PreAllocation.LotSerialNbr && inv.LocationID == this.PreAllocation.LocationID;

                // otherwise, find the smallest piece with sufficient quantity to fulfill the open quantity
                // inventory data is already sorted by quantity available
                // sum up quantity available within the warehouse to determine if it may need Pieced or Backordered
                totalQtyAvail += inv.AdjQtyAvailable;
                // if a single piece with sufficient quantity is found, return it
                return inv.AdjQtyAvailable >= this.OpenQty;
            });

            // did we find a single piece large enough?
            if (suggestedPickingSource) {
                this.SuggestedPickingSource = suggestedPickingSource;
            } else if (totalQtyAvail > this.OpenQty) {
                // sufficient total quantity, but not in a single piece
                this._noSuggestedPieceReason = "(PIECE)";
            } else {
                // insufficient quantity within the warehouse
                this._noSuggestedPieceReason = "(BACK ORD)";
            }
        }

        // Warehouse "03" items
        else {
            // suggest from Default location if available, otherwise location with lowest positive quantity
            inventory = inventory.filter(inv => inv.AdjQtyAvailable > 0).sort((a, b) => {
                if (a.LocationID == this.item!.DefaultIssueFrom) return -1;
                if (b.LocationID == this.item!.DefaultIssueFrom) return 1;
                return (a.AdjQtyAvailable - b.AdjQtyAvailable)
            });

            if (inventory.length) this.SuggestedPickingSource = inventory[0];
        }
    }

    // auto-suggested source for user to pick from
    private _noSuggestedPieceReason: string | null = null;
    SuggestedPickingSource: null | InventorySource = null;
    get SuggestedPickingSourceLocation(): string | null {
        // if no piece reason string (i.e. "(PIECE)" or "(BACKORD)") return that
        // otherwise return the suggested Location ID or empty string if none
        return this._noSuggestedPieceReason || this.SuggestedPickingSource?.LocationID || null;
    }
    get SuggestedPickingSourceSerialLotNbr(): string {
        if (typeof this.SuggestedPickingSource == 'string') return '';
        return (this.SuggestedPickingSource?.LotSerialHash ? (this.SuggestedPickingSource.LotSerialHash + ' - ') : '')
            + (this.SuggestedPickingSource?.LotSerialNbr || '');
    }
    get SuggestedPickingSourceLocationID(): string {
        if (typeof this.SuggestedPickingSource == 'string') return '';
        return this.SuggestedPickingSource?.LocationID || '';
    }

    // #endregion

    // #region Allocation collection within Shipment Detail

    /** emits event when new Allocations are added or removed from the Shipment Detail */
    @Output() OnAllocationsChanged = new EventEmitter();

    NewAllocation(source: InventorySource) {
        let alloc: ShipmentDetailAllocation;

        // Acumatica will create allocations in the Shipment for So pre-allocated lines
        if (this.PreAllocation && this.Allocations.length == 1 && this.Allocations[0].Qty == 0) {
            alloc = this.Allocations[0];
        }
        // if not a Pre-Allocation, create a new object instance
        else {
            alloc = new ShipmentDetailAllocation();
            this.Allocations.push(alloc);
        }

        alloc.source = source;
        this.OnAllocationsChanged.emit();
        return alloc;
    }

    DeleteAllocation(allocation: ShipmentDetailAllocation) {
        let idx = this.Allocations.indexOf(allocation);
        if (idx == -1) throw new Error('Allocation does not exist in collection');

        allocation.source = undefined;
        this.Allocations.remove(allocation);
        this.OnAllocationsChanged.emit();
    }

    /** create and return an array of Allowance objects for all allocations within the shipment detail */
    GetAllowances(): Allowance[] {
        return this.Allocations.reduce((accumulator: Allowance[], allocation) => accumulator.concat(allocation.GetAllowances()), []);
    }

    // #endregion
}

export class PreAllocationDetail extends AcumaticaEntity {
    /*
    Exposed through Acumatica customization into "Pre-Allocated" (non-visible) tab on ShipmentMaint
    When lines are pre-allocated on SO and Shipment is created, each Pre-Allocation comes through as
    a seperate ShipmentDetail line.
    i.e. order 10 Yards but specify Pre-Allocation of a 2 YD and 3 YD piece; Shipment will have 3
    ShipmentDetail lines. One that without Allocations for 5 YD, then the other two with Allocations already defined.
    PreAllocationDetail class contains data indicating the ShipmentDetails that were PreAllocated
    */

    @Acumatica() LineNbr!: number;
    @Acumatica() LotSerialNbr!: string | null;
    @Acumatica() Quantity!: number;
    @Acumatica('Location') LocationID!: string | null;
    // @Acumatica() SplitLineNbr!: number;
}

export class Shipment extends AcumaticaEntity {
    @Acumatica() Type!: string;
    @Acumatica() ShipmentNbr!: string;
    @Acumatica() CustomerID!: string;
    @Acumatica() WarehouseID!: string;
    @Acumatica() note!: string | null;
    @Acumatica() ToWarehouseID?: string;
    @Acumatica() Status!: string;
    @Acumatica() PickerID!: string | null;
    @Acumatica({ write: false }) PickerUsername?: string | null;
    @Acumatica({ format: AcumaticaFieldFormat.Date, write: false }) ShipmentDate?: Date;
    @Acumatica({ format: AcumaticaFieldFormat.Boolean }) CutAndHold!: boolean;
    @Acumatica({ format: AcumaticaFieldFormat.Boolean }) AllowPFAS!: boolean;

    @Acumatica(ShipmentDetail) Details: ShipmentDetail[] = [];
    @Acumatica({ type: PreAllocationDetail, write: false }) PreAllocations?: PreAllocationDetail[] = [];

    // #region helper read-only properties

    get isWhseTransfer() { return this.CustomerID == 'MAIN'; }

    get isComplete(): boolean {
        return this.Details.reduce<boolean>((previous, detail) => previous && detail.isComplete, true);
    }

    get isYardageOrder(): boolean { return this.WarehouseID == '01'; }
    get isSampleOrder(): boolean { return this.WarehouseID == '03'; }
    get totalPickedQty(): number { return this.Details.map(detail => detail.PickedQty).sum(); }

    // #endregion

    /** invoke callback function for every allocation within every shipment detail */
    ForEachAllocation(callback: (allocation: ShipmentDetailAllocation) => void) {
        this.Details.forEach(shipmentDetail => shipmentDetail.Allocations.forEach(callback));
    }

    // #region Transfers & Adjustments

    // track in internal collections because Inventory can be Transfered/Adjusted even if not actively used for picking
    private _transfers: InventorySource[] = [];
    get hasTransfers(): boolean { return !!this._transfers.length; }
    RecordTransfer(source: InventorySource) {
        if (source.isTransfer && !this._transfers.contains(source))
            this._transfers.push(source);
        else if (!source.isTransfer)
            this._transfers.remove(source);
    }

    BuildTransferDocument(): Transfer | null {
        // create & return a Transfor document, even if empty in case it's needed for Cut & Hold

        let transferDocument = new Transfer();
        transferDocument.Description = "Shipment - " + this.ShipmentNbr;
        transferDocument.ToWarehouseID = transferDocument.FromWarehouseID = this.WarehouseID!;

        this._transfers.forEach(source => {
            let transfer = new TransferDetail();
            transfer.InventoryID = source.InventoryID;
            transfer.FromLocationID = source!.OriginalLocationID!;
            transfer.ToLocationID = source!.LocationID!;

            /// transfer.Qty = source!.AdjQtyOnHand + source!.PreAllocatedQty; ///
            /* 
            scenario: 2 Shipments for same item (2 YD & 5YD)
            PO received to DOCK -- QtyOnHand == 30
            cutter works Shipment #1 (2 YD) and returns roll to DOCK
                tansfer.Qty = source.AdjQtyOnHand == 28 (OnHand less this shipment) {CORRECT}
            -- BEFORE OTHER SHIPMENT CONFIRMED --
            cutter works Shipment #2 (5 YD) and transfers to primary
                transfer.Qty = source.AdjQtyOnHand == 25 {INCORRECT!}

            AdjQtyAvailable == 23  (28 less this shipment's 5)
            */
            transfer.Qty = source!.AdjQtyAvailable /* + source!.PreAllocatedQty */;

            transfer.LotSerialNbr = source!.LotSerialNbr;
            transferDocument.Details.push(transfer);
        });

        return transferDocument;
    }

    private _adjustments: InventorySource[] = [];
    get hasAdjustments(): boolean { return !!this._adjustments.length; }
    RecordAdjustment(source: InventorySource) {
        let adj = source!.AdjustmentQty;
        if (adj !== undefined) {
            if (!this._adjustments.contains(source)) this._adjustments.push(source);
        }
        else if (!adj) {
            this._adjustments.remove(source);
        }
    }

    BuildAdjustmentDocument(): Adjustment | null {
        if (!this.hasAdjustments && !this.CutAndHold) return null;

        let adjustmentDocument = new Adjustment();
        adjustmentDocument.Description = "Shipment - " + this.ShipmentNbr;
        this._adjustments.forEach(source => {
            let adjustment = new AdjustmentDetail();
            adjustment.InventoryID = source.InventoryID;
            adjustment.LotSerialNbr = source!.LotSerialNbr;
            adjustment.WarehouseID = this.WarehouseID;
            adjustment.LocationID = source!.OriginalLocationID;
            adjustment.Quantity = source!.AdjustmentAmt + source!.PreAllocatedQty;
            adjustmentDocument.Details.push(adjustment);
        });

        return adjustmentDocument;
    }

    ClearAllAdjustments() {
        this._adjustments.forEach(source => source.AdjustmentQty = undefined);
        this._adjustments = [];
    }
    // #endregion

    // #region Stringify Shipment data for sending to logging service

    normalize(): any {
        return {
            shipment: this.normalize_shipment(),
            transfers: this.BuildTransferDocument(),
            adjustments: this.BuildAdjustmentDocument()
        };
    }

    normalize_shipment(): any {
        let oShipment = this.encode();
        delete oShipment.id;
        delete oShipment.rowNumber;

        this.Details.forEach((detail: ShipmentDetail, index) => {
            let oDetail = oShipment.Details[index] = detail.encode();
            delete oDetail.id;
            delete oDetail.rowNumber;
            oDetail.ShipFullRolls = detail.ShipFullRolls;

            detail.Allocations.forEach((alloc: ShipmentDetailAllocation, index) => {
                let oAlloc: any = alloc.encode();
                delete oAlloc.id;
                delete oAlloc.rowNumber;

                oAlloc.SplitLineNbr = alloc.SplitLineNbr;
                oAlloc.EOR = alloc.EOR;
                oAlloc.EORQty = alloc.EORQty;
                oAlloc.PCS = alloc.PCS;
                oAlloc.PCSQty = alloc.PCSQty;
                oAlloc.FLA = alloc.FLA;
                oAlloc.FLAQty = alloc.FLAQty;
            });
        });

        return oShipment;
    }

    // #endregion
}

export class Allowance {
    MainAllocation: ShipmentDetailAllocation;
    AllowanceAllocation?: ShipmentDetailAllocation;
    Qty: number;
    IsFree: boolean = true;
    note: string | null;
    resetFn: () => void;

    constructor(
        MainAllocation: ShipmentDetailAllocation,
        Qty: number,
        note: string | null = null,
        resetFn: () => void = () => { },
    ) {
        this.MainAllocation = MainAllocation;
        this.Qty = Qty;
        this.note = note;
        this.resetFn = resetFn;
    }

    get shipmentDetail(): ShipmentDetail {
        return this.MainAllocation.shipmentDetail;
    }

    get InventoryID(): string {
        return this.shipmentDetail.InventoryID;
    }
}

// #region Shipment Completion Status / Feedbck

export enum CompleteShipmentStepStatus {
    Omitted,
    Pending,
    InProcess,
    Complete,
    Error
}

@Component({ template: '' })
export class CompleteShipmentStatus {
    @Output() updated: EventEmitter<CompleteShipmentStatus> = new EventEmitter();

    private statusUpdated(resetDetails = true) {
        if (resetDetails) this._Details = null;
        this.updated.emit(this);
    }

    private _error: any = undefined;
    get error(): any { return this._error; }
    set error(error: any) {
        this._error = error;

        // updating statuses will reset Details -- retain it
        let details = this._Details;
        if (this.InventoryAdjustments == CompleteShipmentStepStatus.InProcess) this.InventoryAdjustments = CompleteShipmentStepStatus.Error;
        if (this.ModifySOLines == CompleteShipmentStepStatus.InProcess) this.ModifySOLines = CompleteShipmentStepStatus.Error;
        if (this.AddAllowancesToSO == CompleteShipmentStepStatus.InProcess) this.AddAllowancesToSO = CompleteShipmentStepStatus.Error;
        if (this.GetModifiedSOs == CompleteShipmentStepStatus.InProcess) this.GetModifiedSOs = CompleteShipmentStepStatus.Error;
        if (this.UpdateShipment == CompleteShipmentStepStatus.InProcess) this.UpdateShipment = CompleteShipmentStepStatus.Error;
        if (this.CustomerSpecialRollTags == CompleteShipmentStepStatus.InProcess) this.CustomerSpecialRollTags = CompleteShipmentStepStatus.Error;
        if (this.PrintPacklist == CompleteShipmentStepStatus.InProcess) this.PrintPacklist = CompleteShipmentStepStatus.Error;
        if (this.UploadImages == CompleteShipmentStepStatus.InProcess) this.UploadImages = CompleteShipmentStepStatus.Error;
        if (this.InventoryTransfers == CompleteShipmentStepStatus.InProcess) this.InventoryTransfers = CompleteShipmentStepStatus.Error;

        this.Details = details;
    }

    private _Details: string | null = null;
    get Details() { return this._Details; }
    set Details(value: string | null) { this._Details = value; this.statusUpdated(false); }

    private _InventoryAdjustments: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get InventoryAdjustments() { return this._InventoryAdjustments; }
    set InventoryAdjustments(value) { this._InventoryAdjustments = value; this.statusUpdated(); }

    private _ModifySOLines: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get ModifySOLines() { return this._ModifySOLines; }
    set ModifySOLines(value) { this._ModifySOLines = value; this.statusUpdated(); }

    private _AppendSOLines: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get AddAllowancesToSO() { return this._AppendSOLines; }
    set AddAllowancesToSO(value) { this._AppendSOLines = value; this.statusUpdated(); }

    private _GetModifiedSOs: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get GetModifiedSOs() { return this._GetModifiedSOs; }
    set GetModifiedSOs(value) { this._GetModifiedSOs = value; this.statusUpdated(); }

    // private _AppendShipmentLines: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    // get AddAllowancesToShipment() { return this._AppendShipmentLines; }
    // set AddAllowancesToShipment(value) { this._AppendShipmentLines = value; this.statusUpdated(); }

    private _UpdateShipment: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Pending;
    get UpdateShipment() { return this._UpdateShipment; }
    set UpdateShipment(value) { this._UpdateShipment = value; this.statusUpdated(); }

    private _UploadImages: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get UploadImages() { return this._UploadImages; }
    set UploadImages(value) { this._UploadImages = value; this.statusUpdated(); }

    private _PrintingLabels: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get CustomerSpecialRollTags() { return this._PrintingLabels; }
    set CustomerSpecialRollTags(value) { this._PrintingLabels = value; this.statusUpdated(); }

    private _InventoryTransfers: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get InventoryTransfers() { return this._InventoryTransfers; }
    set InventoryTransfers(value) { this._InventoryTransfers = value; this.statusUpdated(); }

    private _PrintPacklist: CompleteShipmentStepStatus = CompleteShipmentStepStatus.Omitted;
    get PrintPacklist() { return this._PrintPacklist; }
    set PrintPacklist(value) { this._PrintPacklist = value; this.statusUpdated(); }

    constructor();
    constructor(shipment: Shipment, allowances: Array<Allowance>, adjustmentDocument: Adjustment | null, transferDocument: Transfer | null);
    constructor(shipment?: Shipment, allowances?: Array<Allowance>, adjustmentDocument?: Adjustment | null, transferDocument?: Transfer | null) {
        if (shipment === undefined) return;

        this.UploadImages =
            shipment.Details.filter(detail => detail.Allocations.filter(alloc => alloc.FLAImgs.length > 0).length > 0).length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;

        this.InventoryAdjustments = adjustmentDocument?.Details.length
            ? CompleteShipmentStepStatus.Pending
            : CompleteShipmentStepStatus.Omitted;

        this.ModifySOLines =
            shipment.Details.filter(detail => detail.ShipFullRolls).length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;

        // this.AddAllowancesToShipment =
        this.AddAllowancesToSO =
            allowances!.length
                ? CompleteShipmentStepStatus.Pending
                : CompleteShipmentStepStatus.Omitted;

        this.GetModifiedSOs = (this.ModifySOLines == CompleteShipmentStepStatus.Pending || this.AddAllowancesToSO == CompleteShipmentStepStatus.Pending)
            ? CompleteShipmentStepStatus.Pending
            : CompleteShipmentStepStatus.Omitted;

        this.CustomerSpecialRollTags = CompleteShipmentStepStatus.Omitted;

        this.InventoryTransfers = transferDocument?.Details.length
            ? CompleteShipmentStepStatus.Pending
            : CompleteShipmentStepStatus.Omitted;
    }

    public toString() {
        if (this.error) return "ERROR!";
        if (this.InventoryAdjustments == CompleteShipmentStepStatus.InProcess) return "Adjusting Inventory";
        if (this.ModifySOLines == CompleteShipmentStepStatus.InProcess) return "Modifying SO Lines";
        if (this.AddAllowancesToSO == CompleteShipmentStepStatus.InProcess) return "Adding Allowances to SO";
        if (this.GetModifiedSOs == CompleteShipmentStepStatus.InProcess) return "Retrieving Modified SO";
        if (this.UpdateShipment == CompleteShipmentStepStatus.InProcess) return "Updating Shipment";
        if (this.CustomerSpecialRollTags == CompleteShipmentStepStatus.InProcess) return "Printing Customer Labels";
        if (this.PrintPacklist == CompleteShipmentStepStatus.InProcess) return "Printing Pack List";
        if (this.UploadImages == CompleteShipmentStepStatus.InProcess) return "Uploading Images";
        if (this.InventoryTransfers == CompleteShipmentStepStatus.InProcess) return "Creating Location Transfers";
        return "Completed";
    }
}

// #endregion

interface QuickAction { label: string, action: () => void }